"use client";

import {useEffect, useMemo, useState, useTransition} from "react";

const AUDIO_AUTOPLAY_INTENT_KEY = "dah-audio-autoplay-intent";

type PackSummary = {
    id: string;
    name: string;
    visibility: string;
    source: string;
    cardCountBlack: number;
    cardCountWhite: number;
};

export function NewLobbyForm({packs}: {packs: PackSummary[]}) {
    const [playerKind, setPlayerKind] = useState<"session" | "guest" | null>(null);
    const [guestName, setGuestName] = useState("");
    const defaultSelection = useMemo(() => {
        const standardPacks = packs.filter((pack) => pack.visibility === "public").slice(0, 2).map((pack) => pack.id);
        return standardPacks.length > 0 ? standardPacks : packs.slice(0, 2).map((pack) => pack.id);
    }, [packs]);
    const [selectedPackIds, setSelectedPackIds] = useState<string[]>(defaultSelection);
    const [includeBlankCards, setIncludeBlankCards] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isPending, startTransition] = useTransition();
    const publicPacks = useMemo(() => packs.filter((pack) => pack.visibility === "public"), [packs]);
    const privatePacks = useMemo(() => packs.filter((pack) => pack.visibility === "private"), [packs]);

    const totalCards = useMemo(() => {
        return packs
            .filter((pack) => selectedPackIds.includes(pack.id))
            .reduce((sum, pack) => sum + pack.cardCountBlack + pack.cardCountWhite, 0);
    }, [packs, selectedPackIds]);

    useEffect(() => {
        let cancelled = false;

        async function loadPlayer() {
            try {
                const response = await fetch("/api/auth/player", {cache: "no-store"});
                const result = await response.json() as {error?: string; player?: {name: string; kind: "session" | "guest"}};
                if (!response.ok || !result.player) {
                    throw new Error(result.error ?? "Failed to initialize player");
                }
                if (!cancelled) {
                    setPlayerKind(result.player.kind);
                    if (result.player.kind === "guest") {
                        setGuestName(result.player.name);
                    }
                }
            } catch (cause) {
                console.error("Failed to load player for lobby creation.", cause);
                if (!cancelled) {
                    setError(cause instanceof Error ? cause.message : "Failed to initialize player");
                }
            }
        }

        void loadPlayer();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <section className="panel setup-panel">
            <div className="section-kicker">Lobby Setup</div>
            <h2>Pick the deck, then open the doors.</h2>
            <p className="section-copy">
                Public packs open game fast. Private packs let you bring in cursed house rules without publishing them to everybody else.
            </p>
            <div className="pack-library-stack">
                <div className="pack-section-block">
                    <div className="section-heading compact">
                        <div>
                            <span className="section-kicker">Standard Packs</span>
                        </div>
                    </div>
                    <div className="pack-grid">
                        {publicPacks.map((pack) => {
                            const selected = selectedPackIds.includes(pack.id);
                            return (
                                <button
                                    key={pack.id}
                                    className={`pack-card ${selected ? "selected" : ""}`}
                                    onClick={() => {
                                        setSelectedPackIds((current) => current.includes(pack.id)
                                            ? current.filter((id) => id !== pack.id)
                                            : [...current, pack.id]);
                                    }}
                                    type="button"
                                >
                                    <div className="pack-card-top">
                                        <span className="pack-count">{pack.cardCountBlack} black / {pack.cardCountWhite} white</span>
                                        <span className="pill">Standard</span>
                                    </div>
                                    <strong>{pack.name}</strong>
                                </button>
                            );
                        })}
                    </div>
                </div>
                {privatePacks.length > 0 ? (
                    <div className="pack-section-block">
                        <div className="section-heading compact">
                            <div>
                                <span className="section-kicker">Your Private Packs</span>
                            </div>
                        </div>
                        <div className="pack-grid">
                            {privatePacks.map((pack) => {
                                const selected = selectedPackIds.includes(pack.id);
                                return (
                                    <button
                                        key={pack.id}
                                        className={`pack-card ${selected ? "selected" : ""}`}
                                        onClick={() => {
                                            setSelectedPackIds((current) => current.includes(pack.id)
                                                ? current.filter((id) => id !== pack.id)
                                                : [...current, pack.id]);
                                        }}
                                        type="button"
                                    >
                                        <div className="pack-card-top">
                                            <span className="pack-count">{pack.cardCountBlack} black / {pack.cardCountWhite} white</span>
                                            <span className="pill private">Private</span>
                                        </div>
                                        <strong>{pack.name}</strong>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ) : null}
            </div>
            <label className="toggle-row">
                <input
                    checked={includeBlankCards}
                    onChange={(event) => setIncludeBlankCards(event.target.checked)}
                    type="checkbox"
                />
                <span>Include one reusable blank card in every player hand.</span>
            </label>
            {playerKind === "guest" ? (
                <label className="pack-builder-field">
                    <span className="section-kicker">Guest Nickname</span>
                    <input
                        className="join-input pack-builder-input"
                        maxLength={24}
                        onChange={(event) => setGuestName(event.target.value)}
                        placeholder="Pick a nickname"
                        value={guestName}
                    />
                </label>
            ) : null}
            <div className="setup-footer">
                <div>
                    <strong>{selectedPackIds.length}</strong> packs selected
                    <span className="muted"> {totalCards} total cards available</span>
                </div>
                <button
                    className="button button-primary"
                    disabled={isPending || (playerKind === "guest" && guestName.trim().length === 0)}
                    type="button"
                    onClick={() => {
                        startTransition(async () => {
                            setError(null);
                            try {
                                window.sessionStorage.setItem(AUDIO_AUTOPLAY_INTENT_KEY, "1");
                                const response = await fetch("/api/lobbies/create", {
                                    method: "POST",
                                    headers: {"content-type": "application/json"},
                                    body: JSON.stringify({
                                        selectedPackIds,
                                        includeBlankCards,
                                        guestName: playerKind === "guest" ? guestName : undefined
                                    })
                                });
                                const result = await response.json() as {error?: string; lobbyId?: string};
                                if (!response.ok) {
                                    throw new Error(result.error ?? "Failed to create lobby");
                                }
                                window.location.href = `/lobbies/${result.lobbyId}`;
                            } catch (cause) {
                                window.sessionStorage.removeItem(AUDIO_AUTOPLAY_INTENT_KEY);
                                console.error("Failed to create lobby.", cause);
                                setError(cause instanceof Error ? cause.message : "Failed to create lobby");
                            }
                        });
                    }}
                >
                    {isPending ? "Creating..." : "Create lobby"}
                </button>
            </div>
            {error ? <p className="error-text">{error}</p> : null}
        </section>
    );
}
